import { getExternalUrl } from '@change-corgi/core/navigation';

type Options = Readonly<{
	internalRoutes: readonly RegExp[];
}>;

type NavigationUtils = {
	getExternalUrl: (url: string, options?: { forceMode?: 'internal' | 'external' }) => string | undefined;
	/**
	 * One-way switch: once called, every url is treated as external (unless the caller passes `forceMode: 'internal'`),
	 * so the next navigation is a full page load. Client-only (a no-op on the server) and in-memory: a full page load resets it.
	 */
	forceExternalNavigation: () => void;
	/**
	 * Snapshot of the switch, to be used with `subscribeToForcedExternalNavigation` (e.g. `useSyncExternalStore`)
	 */
	isExternalNavigationForced: () => boolean;
	/**
	 * Registers a listener called once when `forceExternalNavigation` is first called. Returns an unsubscribe function.
	 */
	subscribeToForcedExternalNavigation: (listener: () => void) => () => void;
};

class NavigationUtilsImpl implements NavigationUtils {
	private readonly internalRoutes: readonly RegExp[];

	private externalNavigationForced = false;

	private readonly listeners = new Set<() => void>();

	constructor({ internalRoutes }: Options) {
		this.internalRoutes = internalRoutes;
	}

	/**
	 * Returns an url as a string if pointing to an external app/website, otherwise undefined.
	 *
	 * Precedence: explicit `forceMode` > forced external navigation > `internalRoutes`
	 */
	getExternalUrl(url: string, options?: { forceMode?: 'internal' | 'external' }): string | undefined {
		const forceMode = options?.forceMode ?? (this.externalNavigationForced ? 'external' : undefined);
		return getExternalUrl(url, {
			...options,
			...(forceMode ? { forceMode } : {}),
			internalRoutes: this.internalRoutes,
		});
	}

	forceExternalNavigation = (): void => {
		// the server keeps one instance per process, so forcing there would leak into every later request
		if (typeof window === 'undefined' || this.externalNavigationForced) return;
		this.externalNavigationForced = true;
		this.listeners.forEach((listener) => listener());
	};

	isExternalNavigationForced = (): boolean => this.externalNavigationForced;

	subscribeToForcedExternalNavigation = (listener: () => void): (() => void) => {
		this.listeners.add(listener);
		return () => {
			this.listeners.delete(listener);
		};
	};
}

export type { NavigationUtils };

export function createNavigationUtils(options: Options): NavigationUtils {
	return new NavigationUtilsImpl(options);
}

export function createNavigationUtilsFake(errorMsg: string): NavigationUtils {
	const errorFn = () => {
		throw new Error(errorMsg);
	};
	return {
		getExternalUrl: errorFn,
		forceExternalNavigation: errorFn,
		isExternalNavigationForced: errorFn,
		subscribeToForcedExternalNavigation: errorFn,
	};
}
